import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import 'leaflet/dist/leaflet.css';

const ExperienceSection = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const [selectedRating, setSelectedRating] = useState(0);

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const elementVariants = {
    hidden: { 
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, 'Name must be at least 2 characters')
      .required('Name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    place: Yup.string()
      .required('Please select a place'),
    experience: Yup.string()
      .min(20, 'Experience must be at least 20 characters')
      .required('Please share your experience')
  });

  const initialValues = {
    name: '',
    email: '',
    place: '',
    experience: ''
  };

  const afarCenter = [11.7500, 41.0000];
  const tourismPlaces = [
    { name: 'Erta Ale Volcano', position: [13.6000, 40.6667] },
    { name: 'Dallol Depression', position: [14.2417, 40.2889] },
    { name: 'Salt Plains', position: [11.7500, 41.0000] }
  ];

  return (
    <StyledExperience ref={ref}>
      <Container>
        <motion.div
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="experience-wrapper"
        >
          <div className="section-header">
            <motion.span className="section-tag" variants={elementVariants}>
              Share Your Journey
            </motion.span>
            <motion.h2 variants={elementVariants}>
              Experience Afar
            </motion.h2>
          </div>

          <Row className="experience-content">
            <Col lg={6} className="map-column">
              <motion.div className="map-container" variants={elementVariants}>
                <MapContainer 
                  center={afarCenter} 
                  zoom={7} 
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap contributors'
                  />
                  {tourismPlaces.map((place, index) => (
                    <Marker key={index} position={place.position}>
                      <Popup>
                        <div className="custom-popup">
                          <h4>{place.name}</h4>
                          <p>A magnificent destination in Afar</p>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </motion.div>
            </Col>

            <Col lg={6} className="form-column">
              <motion.div className="feedback-form" variants={elementVariants}>
                <h3>Share Your Experience</h3>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={(values, { setSubmitting, resetForm }) => {
                    setTimeout(() => {
                      console.log({ ...values, rating: selectedRating });
                      setSubmitting(false);
                      resetForm();
                      setSelectedRating(0);
                    }, 500);
                  }}
                >
                  {({ isSubmitting }) => (
                    <Form className="formik-form">
                      <div className="form-group">
                        <Field
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          className="form-input"
                        />
                        <ErrorMessage name="name" component="div" className="error-message" />
                      </div>

                      <div className="form-group">
                        <Field
                          type="email"
                          name="email"
                          placeholder="Your Email"
                          className="form-input"
                        />
                        <ErrorMessage name="email" component="div" className="error-message" />
                      </div>

                      <div className="form-group">
                        <Field as="select" name="place" className="form-input">
                          <option value="">Select Place Visited</option>
                          {tourismPlaces.map((place, index) => (
                            <option key={index} value={place.name}>
                              {place.name}
                            </option>
                          ))}
                          <option value="Other">Other</option>
                        </Field>
                        <ErrorMessage name="place" component="div" className="error-message" />
                      </div>

                      <div className="form-group">
                        <Field
                          as="textarea"
                          name="experience"
                          rows={4}
                          placeholder="Share your experience..."
                          className="form-input"
                        />
                        <ErrorMessage name="experience" component="div" className="error-message" />
                      </div>

                      <div className="rating-container">
                        <span>Rate your experience:</span>
                        <div className="stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <motion.button
                              key={star}
                              type="button"
                              className={`star-btn ${star <= selectedRating ? 'active' : ''}`}
                              whileHover={{ scale: 1.2 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setSelectedRating(star)}
                            >
                              {star <= selectedRating ? '★' : '☆'}
                            </motion.button>
                          ))}
                        </div>
                      </div>

                      <motion.button 
                        type="submit"
                        className="submit-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Experience'}
                      </motion.button>
                    </Form>
                  )}
                </Formik>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </StyledExperience>
  );
};

const StyledExperience = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  .section-header {
    text-align: center;
    margin-bottom: 60px;

    .section-tag {
      display: inline-block;
      padding: 8px 20px;
      background: linear-gradient(45deg, #2d7ba8, #34495e);
      color: white;
      border-radius: 50px;
      font-size: 0.9rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 20px;
    }

    h2 {
      font-size: 3.5rem;
      font-weight: 700;
      background: linear-gradient(45deg, #1e293b, #334155);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin: 0;
    }
  }

  .experience-content {
    margin-top: 40px;
  }

  .map-column {
    .map-container {
      height: 600px;
      border-radius: 20px;
      overflow: hidden;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    }
  }

  .form-column {
    .feedback-form {
      background: white;
      padding: 40px;
      border-radius: 20px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      height: 600px;
      overflow-y: auto;

      h3 {
        font-size: 2rem;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 30px;
      }

      .form-group {
        margin-bottom: 20px;
      }

      .error-message {
        color: #dc3545;
        font-size: 0.875rem;
        margin-top: 5px;
      }

      .form-input {
        width: 100%;
        border: 2px solid #e2e8f0;
        border-radius: 12px;
        padding: 12px 20px;
        font-size: 1rem;
        transition: all 0.3s ease;

        &:focus {
          border-color: #2d7ba8;
          box-shadow: 0 0 0 3px rgba(45, 123, 168, 0.1);
          outline: none;
        }
      }

      .rating-container {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 20px;

        .stars {
          display: flex;
          gap: 5px;
        }

        .star-btn {
          background: none;
          border: none;
          padding: 5px;
          cursor: pointer;
          font-size: 1.5rem;
          color: #ffd700;
          
          &.active {
            color: #ffd700;
          }
        }
      }

      .submit-btn {
        width: 100%;
        padding: 15px;
        background: linear-gradient(45deg, #2d7ba8, #34495e);
        color: white;
        border: none;
        border-radius: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(45, 123, 168, 0.2);
        }

        &:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      }
    }
  }

  .custom-popup {
    text-align: center;
    
    h4 {
      margin: 0;
      color: #2d7ba8;
      font-size: 1.1rem;
    }

    p {
      margin: 5px 0 0;
      font-size: 0.9rem;
      color: #666;
    }
  }

  @media (max-width: 992px) {
    padding: 80px 0;

    .section-header h2 {
      font-size: 2.5rem;
    }

    .map-column .map-container,
    .form-column .feedback-form {
      height: 400px;
      margin-bottom: 30px;
    }
  }

  @media (max-width: 768px) {
    .section-header {
      margin-bottom: 40px;
    }
  }
`;

export default ExperienceSection;